import {
  BufferAttribute,
  BufferGeometry,
  DoubleSide,
  MathUtils,
  Mesh,
  NormalBlending,
  ShaderMaterial,
  Vector3,
  type Color,
  type Object3D,
  type Texture,
} from 'three'

import {
  LEADIN_APPROACH_DEG,
  LEADIN_FADE_SECONDS,
  LEADIN_LATERAL_RATIO,
  LEADIN_POP_SECONDS,
  LEADIN_SPLAY_DEG,
  LEADIN_STEP_COUNT,
  LEADIN_STEP_INTERVAL_SECONDS,
  LEADIN_STRIDE_RATIO,
  LEADIN_TOTAL_SECONDS,
  PAW_HALO_RATIO,
  PAW_LIFT_M,
  PAW_OPACITY,
  PAW_POP_BACK,
  PAW_PRINT_SIZE_RATIO,
} from '../config'
import {easeInCubic, ramp01} from '../util/easing'

/**
 * The paw prints that land one after another before the character arrives.
 *
 * Holds no clock. It is told what second to be at and draws that - the same
 * split as `shockwave-ring` and `spark-burst`, with `placement-sequence.ts`
 * owning the beat sheet the way `entrance-effect.ts` owns the landing's.
 *
 * NOT ADDITIVE, and it is the only thing in src/effects/ that is not. Additive
 * vanishes on a bright floor and multiply vanishes on a dark one, because both
 * are operations whose result depends on how bright the camera feed happens to
 * be - the one quantity AR cannot know. Ordinary alpha REPLACES, so a print
 * looks the same on any floor, and a black halo baked into the mask's green
 * channel separates it from a light one. See src/config.ts's PAW_DARKEN for the
 * measurements behind that.
 *
 * All `LEADIN_STEP_COUNT` prints are one draw call: quads baked into a single
 * geometry, with the per-print birth time as a vertex attribute. The pop is
 * evaluated in closed form from `uTime` in the vertex shader, never integrated,
 * so one `step(0.5)` is identical to thirty `step(1/60)` and /debug can check
 * the curve deterministically. That is the same contract spark-burst keeps, and
 * for the same reason.
 *
 * Deliberately NO randomness. `MathUtils.seededRandom` keeps its seed in a
 * module-global and `spark-burst.ts` is documented as its only user; a second
 * one would break the "the Nth placement looks the same every session"
 * guarantee for both. The layout here is a pure function of the print index,
 * which is also just correct - a walk is regular.
 *
 * PARENTING: belongs under `PlacementController.root`, added after
 * `setContent()`. The root's yaw is what points the trail at the camera.
 */

export interface FootprintTrailDebug {
  /** Whatever `setElapsed` was last handed, including out-of-range values. */
  readonly elapsedSeconds: number
  /**
   * The global fade, computed on the CPU.
   *
   * The one number in this effect a headless check can read back - everything
   * else about a print lives in the shader - which is what makes the
   * determinism check in /debug a check rather than a claim.
   */
  readonly opacity: number
  /** How many prints have been born at `elapsedSeconds`. 0..LEADIN_STEP_COUNT. */
  readonly bornCount: number
  readonly visible: boolean
}

export interface FootprintTrail {
  /** Parent this wherever the lead-in lives. */
  readonly object: Object3D
  /**
   * Seconds since the first print landed. Negative, or past the trail's last
   * beat, hides it - so the caller never has to special-case "not running".
   */
  setElapsed(seconds: number): void
  readonly debug: FootprintTrailDebug
  dispose(): void
}

export interface FootprintTrailOptions {
  /**
   * The shared paw mask: R is the print, G is its halo.
   *
   * Borrowed, never owned. `dispose()` here does not touch it: `app-module.ts`
   * builds it and frees it, so a campaign that later loads bespoke art can swap
   * this for a fetched texture without any of the lifetime rules changing.
   */
  readonly mask: Texture
  /** Already resolved and in the working colour space, like the ring's. */
  readonly color: Color
  /** `NormalizedModel.groundRadius`. Sizes the print, the stride and the track. */
  readonly groundRadius: number
}

const vertexShader = /* glsl */ `
attribute vec2 aCorner;   // corner offset from this print's centre, in metres, XZ
attribute float aBirth;   // seconds after the trail starts that this print lands

uniform float uTime;
uniform float uOpacity;

varying vec2 vUv;
varying float vAlpha;

void main() {
  float t = uTime - aBirth;

  // easeOutBack, peaking at ~1.10. Closed form and evaluated per vertex rather
  // than written into three matrices on the CPU every frame - the same reason
  // spark-burst evaluates its ballistics here. This is also why the curve is not
  // mirrored into src/util/easing.ts: it would have no TypeScript caller.
  float x = clamp(t / ${LEADIN_POP_SECONDS.toFixed(4)}, 0.0, 1.0);
  float k = x - 1.0;
  float pop = 1.0 + ${(PAW_POP_BACK + 1).toFixed(5)} * k * k * k
                  + ${PAW_POP_BACK.toFixed(5)} * k * k;

  vUv = uv;
  // No separate fade-in: the pop is exactly 0 at t = 0, so an unborn print is a
  // degenerate quad that rasterises nothing. The ink lands at full strength and
  // the shape overshoots, which is what makes it read as a stamp rather than as
  // something dissolving into view.
  vAlpha = step(0.0, t) * uOpacity;

  // The pop scales the CORNER about the centre, not the whole quad about the
  // origin - the position attribute holds this print's centre, repeated for all
  // four vertices.
  vec3 p = position + vec3(aCorner.x, 0.0, aCorner.y) * pop;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
}
`

const fragmentShader = /* glsl */ `
uniform sampler2D uMask;
uniform vec3 uColor;

varying vec2 vUv;
varying float vAlpha;

void main() {
  // R is the print, G is the halo drawn around and under it. Multiplying the
  // halo by (1 - body) is what keeps the green that sits beneath the print from
  // doubling up on it.
  vec2 mask = texture2D(uMask, vUv).rg;
  float body = mask.r * vAlpha;
  float halo = mask.g * (1.0 - mask.r) * vAlpha * ${PAW_HALO_RATIO.toFixed(3)};

  gl_FragColor = vec4(uColor, body);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
  // Premultiply AFTER the transfer function, matching the chunk order in
  // three's own meshbasic_frag. Premultiplying linear values and then encoding
  // them is a different, wrong picture, because the OETF is not linear.
  gl_FragColor.rgb *= gl_FragColor.a;
  // The halo is pure black, so it adds nothing to rgb and only raises alpha.
  // Under premultiplied blending that composites as "coloured print over black
  // halo over camera feed" exactly, in one pass and one draw call.
  gl_FragColor.a = min(gl_FragColor.a + halo, 1.0);
}
`

/** Past this the trail is gone. Mirrors the beat sheet in src/config.ts. */
const LAST_BEAT_SECONDS = LEADIN_TOTAL_SECONDS + LEADIN_FADE_SECONDS

/** Four corners as (u, v) pairs, wound so the quad faces up in XZ. */
const CORNERS: readonly (readonly [number, number])[] = [
  [0, 0],
  [1, 0],
  [1, 1],
  [0, 1],
]

export const createFootprintTrail = ({
  mask,
  color,
  groundRadius,
}: FootprintTrailOptions): FootprintTrail => {
  const vertexCount = LEADIN_STEP_COUNT * 4

  const positions = new Float32Array(vertexCount * 3)
  const corners = new Float32Array(vertexCount * 2)
  const uvs = new Float32Array(vertexCount * 2)
  const births = new Float32Array(vertexCount)
  const indices = new Uint16Array(LEADIN_STEP_COUNT * 6)

  const bearing = MathUtils.degToRad(LEADIN_APPROACH_DEG)
  // Unit vector from the placement point toward where the trail begins. The
  // placement root's local +Z faces the camera (see hit-test.ts's yawToFace), so
  // a bearing past 90 puts the trail behind the character.
  const approach = new Vector3(Math.sin(bearing), 0, Math.cos(bearing))
  const half = groundRadius * PAW_PRINT_SIZE_RATIO * 0.5

  const centre = new Vector3()
  const forward = new Vector3()
  const across = new Vector3()

  for (let i = 0; i < LEADIN_STEP_COUNT; i++) {
    // i = 0 is the first print to land and the furthest away. The nearest stops
    // one stride short of the placement point rather than on it, so the last
    // print is not simply buried under the character's feet.
    const along = groundRadius * LEADIN_STRIDE_RATIO * (LEADIN_STEP_COUNT - i)
    // +1 is the left foot, and it is offset to the left as well as mirrored.
    // Using ONE value for both is the point: split them and the trail ends up
    // with a left-shaped print sitting on the right, which nobody can name but
    // everybody reads as wrong.
    const side = i % 2 === 0 ? 1 : -1

    // Toes point the way the walk is going, which is back down the approach.
    const heading = bearing + Math.PI + side * MathUtils.degToRad(LEADIN_SPLAY_DEG)
    forward.set(Math.sin(heading), 0, Math.cos(heading))
    // `forward` rotated +90 degrees about Y.
    across.set(forward.z, 0, -forward.x)

    centre
      .copy(approach)
      .multiplyScalar(along)
      .addScaledVector(across, side * groundRadius * LEADIN_LATERAL_RATIO)

    for (let c = 0; c < 4; c++) {
      const corner = CORNERS[c]
      if (corner === undefined) continue
      const [cu, cv] = corner
      const v = i * 4 + c

      positions[v * 3] = centre.x
      positions[v * 3 + 1] = 0
      positions[v * 3 + 2] = centre.z

      // Laid out in the print's own frame, so the pop in the shader is a plain
      // scalar multiply and needs no rotation of its own.
      const ax = (cu - 0.5) * 2 * half
      const az = (cv - 0.5) * 2 * half
      corners[v * 2] = across.x * ax + forward.x * az
      corners[v * 2 + 1] = across.z * ax + forward.z * az

      // Mirroring lives entirely in the uv. Negating a geometry axis instead
      // would flip the winding, and there is one material for all the prints.
      uvs[v * 2] = side > 0 ? cu : 1 - cu
      // The mask is drawn with the toes at the TOP of the canvas and `flipY`
      // is on, so v = 1 is the toe end - which is +forward here.
      uvs[v * 2 + 1] = cv

      births[v] = i * LEADIN_STEP_INTERVAL_SECONDS
    }

    const base = i * 4
    const tri = i * 6
    indices[tri] = base
    indices[tri + 1] = base + 1
    indices[tri + 2] = base + 2
    indices[tri + 3] = base
    indices[tri + 4] = base + 2
    indices[tri + 5] = base + 3
  }

  const geometry = new BufferGeometry()
  geometry.setAttribute('position', new BufferAttribute(positions, 3))
  geometry.setAttribute('aCorner', new BufferAttribute(corners, 2))
  geometry.setAttribute('uv', new BufferAttribute(uvs, 2))
  geometry.setAttribute('aBirth', new BufferAttribute(births, 1))
  geometry.setIndex(new BufferAttribute(indices, 1))

  const uniforms = {
    uMask: {value: mask},
    uColor: {value: color},
    uTime: {value: 0},
    uOpacity: {value: 0},
  }

  const material = new ShaderMaterial({
    uniforms,
    vertexShader,
    fragmentShader,
    transparent: true,
    // See the header. This is the deliberate break from the ring and the sparks.
    blending: NormalBlending,
    // Lets the coloured body and the black halo composite exactly in one pass.
    // The shader does the multiply itself - this flag only picks the blend func.
    premultipliedAlpha: true,
    // Tests but does not write, like the ring: real geometry in front must still
    // occlude a print, but three coplanar transparent quads must not fight each
    // other where they overlap.
    depthWrite: false,
    // Costs no extra fragments and keeps the trail visible when a table-top
    // placement is looked at from slightly below - the same call the ring makes.
    side: DoubleSide,
  })

  const mesh = new Mesh(geometry, material)
  mesh.name = 'lead-in-footprints'
  // ShadowMaterial does not disable depth writes, so the catcher at y = 0.001
  // writes depth and this has to clear it. Below the ring's RING_LIFT_M.
  mesh.position.y = PAW_LIFT_M
  // Shadow 0, prints 1, ring 2, sparks 3: matter on top of shadow, light on top
  // of matter. Neither this nor the ring writes depth, so this ordering is the
  // only thing deciding which wins - distance sorting alone would swap them
  // frame to frame.
  mesh.renderOrder = 1
  // `position` holds print CENTRES and the vertex shader builds the corners, so
  // a bounding sphere computed from the attribute would be far too small and
  // would cull prints that are plainly on screen. Same trap as spark-burst's
  // displaced points, and with three quads there is nothing to save by culling.
  mesh.frustumCulled = false
  mesh.visible = false

  let elapsed = 0

  return {
    object: mesh,

    setElapsed: (seconds) => {
      elapsed = seconds
      if (seconds < 0 || seconds > LAST_BEAT_SECONDS) {
        mesh.visible = false
        return
      }
      mesh.visible = true
      uniforms.uTime.value = seconds
      // On the CPU deliberately, and the only part of this effect that is.
      // Per-print state has to be per-vertex, but the trail fades as ONE thing -
      // fading print by print would read as the trail being wiped from the back -
      // and doing it here leaves /debug exactly one number to compare.
      uniforms.uOpacity.value =
        PAW_OPACITY *
        (1 - easeInCubic(ramp01(seconds, LEADIN_TOTAL_SECONDS, LAST_BEAT_SECONDS)))
    },

    get debug() {
      return {
        elapsedSeconds: elapsed,
        opacity: uniforms.uOpacity.value,
        // Read off the same schedule the attribute was written from, rather
        // than a second copy of it.
        bornCount:
          elapsed < 0
            ? 0
            : Math.min(LEADIN_STEP_COUNT, Math.floor(elapsed / LEADIN_STEP_INTERVAL_SECONDS) + 1),
        visible: mesh.visible,
      }
    },

    dispose: () => {
      mesh.removeFromParent()
      geometry.dispose()
      material.dispose()
      // `mask` is deliberately NOT disposed - see FootprintTrailOptions.
    },
  }
}
